/**
 * Indian formatting — a hard requirement (§13.1), so it lives in exactly one
 * place. ₹1,23,456.78 (lakh grouping, not thousands), dates as 05 Jul 2026,
 * fiscal years as FY 2026-27.
 *
 * Money arrives from the API as a STRING (§4) and is formatted here without
 * ever becoming a float. Number(x) on a rupee value is a bug: it is precisely
 * how ₹0.01 goes missing from a cashback total.
 */

const inr = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const inrCompact = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const plain = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** ₹1,23,456.78 — the default for any money on screen. */
export function formatMoney(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "—";
  return inr.format(n);
}

/** ₹1,23,457 — for axis ticks and dense tables, where paise are noise. */
export function formatMoneyCompact(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "—";
  return inrCompact.format(n);
}

/** 1,23,456.78 — no symbol, for when the column header already says ₹. */
export function formatAmount(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n)) return "—";
  return plain.format(n);
}

/**
 * 05 Jul 2026 (§13.1).
 *
 * The month name comes from `MONTH_NAMES`, not from `Intl`'s `month: "short"`.
 * ICU renders September as **"Sept"** in en-IN — four letters where every other
 * month gets three, which breaks the format §13.1 specifies and knocks a column
 * of dates out of alignment for one month in twelve. `Intl` still does the
 * timezone work; only the month label is ours.
 */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  const parts = IST_PARTS.formatToParts(d);
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${part("day")} ${MONTH_NAMES[Number(part("month")) - 1]} ${part("year")}`;
}

/** Numeric parts in IST, so the month can be labelled from MONTH_NAMES. */
const IST_PARTS = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "Asia/Kolkata",
});

/** 05 Jul 2026, 4:32 pm — for alert timestamps, which keep a time (§4). */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  // Date half shares `formatDate` so the September spelling is fixed in one
  // place rather than two that can drift.
  return `${formatDate(d)}, ${IST_TIME.format(d)}`;
}

const IST_TIME = new Intl.DateTimeFormat("en-IN", {
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZone: "Asia/Kolkata",
});

/**
 * "2026-07" → "Jul 2026". Built from the label's own parts, never `new Date()`
 * on a bare month string — that parses as UTC midnight and renders as the
 * *previous* month for anyone west of Greenwich.
 */
export function formatMonth(label: string): string {
  const [year, month] = label.split("-");
  const name = MONTH_NAMES[Number(month) - 1];
  return year && name ? `${name} ${year}` : label;
}

/** "2026-07" → "Jul" — for axis ticks, where the year is on the axis itself. */
export function formatMonthShort(label: string): string {
  return MONTH_NAMES[Number(label.split("-")[1]) - 1] ?? label;
}

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** "FY2026-27" → "FY 2026-27" (§13.1). */
export function formatFiscalYear(label: string): string {
  return label.startsWith("FY") ? `FY ${label.slice(2)}` : label;
}

/** "in 3 days" / "2 days ago" / "today" — powers due-date urgency copy (§12). */
export function formatRelativeDays(target: string | Date | null | undefined): string {
  if (!target) return "—";
  const d = typeof target === "string" ? new Date(target) : target;
  if (Number.isNaN(d.getTime())) return "—";
  const days = daysUntil(d);
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  if (days === -1) return "yesterday";
  return days > 0 ? `in ${days} days` : `${Math.abs(days)} days ago`;
}

/**
 * "just now" / "12 minutes ago" / "3 hours ago" / "05 Jul 2026, 4:32 pm".
 *
 * Separate from `formatRelativeDays` because it answers a different question.
 * Due dates live on a scale of days, so "in 3 days" is the useful unit. A sync
 * that ran forty minutes ago is a different kind of fact — "today" would be
 * true and useless. Past a day, the exact timestamp beats a fuzzy count.
 */
export function formatRelativeTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";

  const seconds = Math.round((Date.now() - d.getTime()) / 1000);
  if (seconds < 0) return formatDateTime(d); // clock skew — don't invent a future
  if (seconds < 60) return "just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

  return formatDateTime(d);
}

/** Whole days from today to `target`, in Asia/Kolkata. Negative = past. */
export function daysUntil(target: string | Date): number {
  const d = typeof target === "string" ? new Date(target) : target;
  const toIST = (x: Date) =>
    new Date(x.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  const a = toIST(new Date());
  const b = toIST(d);
  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

export type Urgency = "overdue" | "due-soon" | "due-week" | "later" | "none";

/** §12: overdue / ≤3 days / ≤7 days / later. Drives dashboard colour coding. */
export function urgencyOf(dueDate: string | Date | null | undefined): Urgency {
  if (!dueDate) return "none";
  const days = daysUntil(dueDate);
  if (days < 0) return "overdue";
  if (days <= 3) return "due-soon";
  if (days <= 7) return "due-week";
  return "later";
}

/** Mask a last4 the way a card face reads: •••• 4321. */
export function formatLast4(last4: string): string {
  return `•••• ${last4}`;
}

// --- Calendar arithmetic on ISO dates ----------------------------------------
// The API sends plain "YYYY-MM-DD" days. Doing the arithmetic in UTC keeps a
// day a day: local-time Date maths drifts across DST and timezone edges.

function isoParts(iso: string): [number, number, number] {
  const [y = 1970, m = 1, d = 1] = iso.slice(0, 10).split("-").map(Number);
  return [y, m, d];
}

/** "2026-11-03" + 2 → "2026-11-05". */
export function addDaysISO(iso: string, days: number): string {
  const [y, m, d] = isoParts(iso);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** "2026-11-03" → "Tuesday". */
export function weekdayName(iso: string): string {
  const [y, m, d] = isoParts(iso);
  return WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] ?? "";
}

/** "2026-11-03" → { day: 3, weekday: 2, month: "Nov" } — for calendar cells. */
export function dayParts(iso: string): { day: number; weekday: number; month: string } {
  const [y, m, d] = isoParts(iso);
  return {
    day: d,
    weekday: new Date(Date.UTC(y, m - 1, d)).getUTCDay(),
    month: MONTH_NAMES[m - 1] ?? "",
  };
}

const NUMBER_WORDS = [
  "no", "one", "two", "three", "four", "five", "six",
  "seven", "eight", "nine", "ten", "eleven", "twelve",
];

/** 2 → "two"; 14 → "14". For sentences, where small counts read as words. */
export function numberWord(n: number): string {
  return NUMBER_WORDS[n] ?? String(n);
}
